import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsCheckboxModule, DsTooltipDirectiveModule } from '@bmw-ds/components';

import { LanguagePickerComponent } from '../../../form/components/language-picker/language-picker.component';
import { I18nService, useI18n } from '../../../i18n/services/i18n.service';
import { trackInputInclusiveUndefined } from '../../../utils/signal/trackInput';
import { GcdmLanguageProvider } from '../../services/gcdm-language.provider';

@Component({
  selector: 'cdf-gcdm-language-container',
  imports: [LanguagePickerComponent, DsCheckboxModule, FormsModule, DsTooltipDirectiveModule],
  host: {
    // Hide the element itself if there are no languages to display,
    // so that it does not have padding inside a flex container with gaps.
    '[class.d-none]': '!displayLanguagePicker()',
  },
  template: `
    @if (displayLanguagePicker()) {
      <cdf-language-picker
        [label]="label()"
        [languages]="gcdmLanguageProvider.availableConsentLanguagesInThisCountry()"
        [language]="gcdmLanguageProvider.resolvedConsentLanguage()"
        (languageChange)="gcdmLanguageProvider.languagePickerSelection.set($event)"
        [disabled]="disableLanguagePicker()"
        [ds-tooltip]="tooltip()"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GcdmLanguageContainer {
  private readonly i18n = useI18n();
  private readonly i18nService = inject(I18nService);
  protected readonly gcdmLanguageProvider = inject(GcdmLanguageProvider);
  readonly label = input.required<string>();

  readonly correspondenceLanguage = input.required<string | undefined>();
  protected readonly propagateCorrespondenceLanguage = trackInputInclusiveUndefined(
    this.correspondenceLanguage,
    this.gcdmLanguageProvider.correspondenceLanguage,
  );

  protected readonly displayLanguagePicker = computed(() => {
    const uiLanguage = this.i18nService.activeLanguage().substring(0, 2);
    const gcdmLanguages = this.gcdmLanguageProvider.gcdmLanguages();
    return gcdmLanguages.length > 1 || (gcdmLanguages.length === 1 && gcdmLanguages[0] !== uiLanguage);
  });

  protected readonly disableLanguagePicker = computed(() => {
    return this.gcdmLanguageProvider.gcdmLanguages().length === 1;
  });

  protected readonly tooltip = computed(() => {
    if (this.gcdmLanguageProvider.gcdmLanguages().length === 1) {
      return this.i18n('consent.noOtherLanguages', {
        language: this.gcdmLanguageProvider.gcdmLanguages()[0].toUpperCase(),
      });
    }
    return '';
  });
}
